// ============================================
// RutinaResultado — pinta la rutina que arma el motor (/store/rutina):
// mañana / noche, calendario de noches, pasos que faltan y avisos.
// Lo usa el diagnóstico; el modo "por producto" lo reutilizará tal cual.
// ============================================

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import './RutinaResultado.css';

const DIAS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

function textoDias(dias) {
  if (!dias) return 'Todos los días';
  const nombres = dias.map((d) => DIAS[d]);
  const lista = nombres.length > 1
    ? `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`
    : nombres[0];
  return lista.charAt(0).toUpperCase() + lista.slice(1);
}

function Paso({ n, item, producto }) {
  return (
    <li className="rr-step">
      <span className="rr-step__n">{n}</span>
      {producto?.image ? (
        <Link to={`/products/${item.handle}`} className="rr-step__img">
          <img src={producto.image} alt={item.nombre} loading="lazy" />
        </Link>
      ) : (
        <span className="rr-step__img rr-step__img--empty" />
      )}
      <div className="rr-step__body">
        <p className="rr-step__paso">{item.pasoNombre}</p>
        <Link to={`/products/${item.handle}`} className="rr-step__name">{producto?.name || item.nombre}</Link>
        <p className="rr-step__meta">{[item.cantidad, item.zona].filter(Boolean).join(' · ')}</p>
        {item.dias && <p className="rr-step__dias">{textoDias(item.dias)}</p>}
        {item.nota && <p className="rr-step__nota">{item.nota}</p>}
      </div>
    </li>
  );
}

export default function RutinaResultado({ rutina, productos = [], carrito = [] }) {
  const { addToCart } = useCart();
  const [momento, setMomento] = useState('manana');
  const [agregando, setAgregando] = useState(false);
  const [agregado, setAgregado] = useState(false);

  const porHandle = useMemo(
    () => Object.fromEntries(productos.map((p) => [p.handle, p])),
    [productos]
  );

  const items = momento === 'manana' ? rutina.manana : rutina.noche;
  const nombreDe = (h) => porHandle[h]?.name || rutina.noche.find((i) => i.handle === h)?.nombre || h;
  const enCalendario = new Set(rutina.noche.filter((i) => i.dias).map((i) => i.handle));

  const agregar = async (handles) => {
    setAgregando(true);
    try {
      for (const h of handles) {
        const p = porHandle[h];
        const variantId = p?.variants?.[0]?.id;
        if (variantId) await addToCart(p, variantId, 1);
      }
      setAgregado(true);
    } finally {
      setAgregando(false);
    }
  };

  return (
    <div className="rr">
      <div className="rr-tabs" role="tablist">
        {[['manana', 'Mañana'], ['noche', 'Noche']].map(([id, label]) => (
          <button
            key={id}
            role="tab"
            aria-selected={momento === id}
            className={`rr-tab ${momento === id ? 'is-active' : ''}`}
            onClick={() => setMomento(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <ol className="rr-steps">
        {items.map((item, i) => (
          <Paso key={item.handle} n={i + 1} item={item} producto={porHandle[item.handle]} />
        ))}
      </ol>

      {momento === 'manana' && rutina.cualquierMomento.length > 0 && (
        <div className="rr-extra">
          <p className="rr-label">En cualquier momento</p>
          <ol className="rr-steps">
            {rutina.cualquierMomento.map((item) => (
              <Paso key={item.handle} n="·" item={item} producto={porHandle[item.handle]} />
            ))}
          </ol>
        </div>
      )}

      {momento === 'noche' && rutina.calendario && (
        <div className="rr-cal">
          <p className="rr-label">Tu semana</p>
          <div className="rr-cal__grid">
            {rutina.calendario.map((d) => {
              const activos = d.handles.filter((h) => enCalendario.has(h));
              return (
                <div key={d.dia} className={`rr-cal__day ${d.descanso ? 'is-rest' : ''}`}>
                  <span className="rr-cal__name">{d.nombre.slice(0, 3)}</span>
                  {activos.map((h) => <span key={h} className="rr-cal__item">{nombreDe(h)}</span>)}
                  {d.descanso && <span className="rr-cal__item rr-cal__item--rest">Descanso</span>}
                </div>
              );
            })}
          </div>
          <p className="rr-cal__hint">Los días de descanso tu piel se recupera solo con limpieza e hidratación.</p>
        </div>
      )}

      {rutina.sugerencias.length > 0 && (
        <div className="rr-sug">
          <p className="rr-label">Para completar tu rutina</p>
          {rutina.sugerencias.map((s) => (
            <div key={s.handle} className="rr-sug__row">
              <div>
                <Link to={`/products/${s.handle}`} className="rr-step__name">{porHandle[s.handle]?.name || s.nombre}</Link>
                <p className="rr-step__nota">{s.motivo}</p>
              </div>
              <button className="rr-btn rr-btn--ghost" disabled={agregando} onClick={() => agregar([s.handle])}>
                Agregar
              </button>
            </div>
          ))}
        </div>
      )}

      {rutina.avisos.length > 0 && (
        <ul className="rr-avisos">
          {rutina.avisos.map((a) => <li key={a}>{a}</li>)}
        </ul>
      )}

      <p className="rr-legal">
        Esta rutina es una guía orientativa basada en tus respuestas. Tu dermatólogo o cosmetóloga de confianza
        siempre debe valorar tu piel.
      </p>

      {carrito.length > 0 && (
        <div className="rr-cta">
          <button className="rr-btn" disabled={agregando} onClick={() => agregar(carrito)}>
            {agregando ? 'Agregando…' : agregado ? 'Rutina agregada ✓' : 'Agregar mi rutina al carrito'}
          </button>
          {agregado && <Link to="/cart" className="rr-cta__link">Ver carrito</Link>}
        </div>
      )}
    </div>
  );
}
